import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from '@mui/material';
import { AgencyAgreementGenerationTermsContent } from './AgencyAgreementGenerationTermsContent';

const SCROLL_THRESHOLD = 50;

interface AgencyAgreementTermsDialogProps {
  open: boolean;
  onClose: () => void;
  onAccept: () => void;
}

/**
 * Dialog that shows the Terms of Service for the Generate Agreement (Platform) service.
 * User must scroll to the bottom before accepting.
 */
export const AgencyAgreementTermsDialog = ({ open, onClose, onAccept }: AgencyAgreementTermsDialogProps) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    if (distanceFromBottom <= SCROLL_THRESHOLD && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  }, [hasScrolledToBottom]);

  const handleClose = () => {
    setHasScrolledToBottom(false);
    onClose();
  };

  const handleAccept = () => {
    setHasScrolledToBottom(false);
    onAccept();
    onClose();
  };

  useEffect(() => {
    if (!open) {
      setHasScrolledToBottom(false);
      return;
    }
    const checkFits = () => {
      const el = scrollRef.current;
      if (el && el.scrollHeight <= el.clientHeight + SCROLL_THRESHOLD) {
        setHasScrolledToBottom(true);
      }
    };
    const timer = window.setTimeout(checkFits, 100);
    const timer2 = window.setTimeout(checkFits, 400);
    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(timer2);
    };
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { maxHeight: '90vh' } }}
    >
      <DialogTitle>Terms of Service — Generate Agreement (Platform)</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2, color: 'text.secondary' }}>
          Please read and accept these terms before generating an agency agreement.
        </Typography>
        <Box
          ref={scrollRef}
          onScroll={handleScroll}
          sx={{
            maxHeight: 420,
            overflowY: 'auto',
            pr: 1,
          }}
        >
          <AgencyAgreementGenerationTermsContent />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Please scroll to the bottom to confirm you have read the Terms of Service.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleAccept} disabled={!hasScrolledToBottom}>
            I have read and accept
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
